import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-surface dark:bg-surface-dark py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            About Vegetablah.com
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Empowering you to achieve your health goals with AI-powered guidance, comprehensive tracking from fitness devices and manual inputs, and insightful analysis.
          </p>
        </div>

        {/* Mission Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl text-gray-900 dark:text-white">Our Mission</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              At Vegetablah.com, we believe that understanding your health patterns is the first step toward making meaningful improvements. 
              Our platform provides you with the tools and insights needed to connect your fitness devices, manually input health data, track your food intake, monitor your health metrics, 
              set personalized health goals, and use AI and machine learning to receive tailored advice on making progress toward those goals.
            </p>
          </CardContent>
        </Card>

        {/* Features Overview */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl text-gray-900 dark:text-white">What We Offer</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Food Logging</h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Track your daily food intake with detailed nutritional information and portion sizes.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Health Monitoring</h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Record and monitor various health metrics including weight, mood, energy levels, and more.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Fitness Device Integration</h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Connect your fitness devices to automatically import and track health data seamlessly.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">AI-Powered Goal Guidance</h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Set personalized health goals and receive AI-driven advice based on your data inputs and outputs to make progress toward achieving them.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Correlation Analysis</h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Discover patterns and correlations between your food choices and health outcomes.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Data Visualization</h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Visualize your health journey with comprehensive charts and trend analysis.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Technology Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl text-gray-900 dark:text-white">Technology & Privacy</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                Vegetablah.com is built with modern web technologies, including AI and machine learning integrations, to ensure a fast, secure, and reliable experience. 
                We prioritize your privacy and data security, implementing industry-standard encryption and security measures 
                to protect your personal health information.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                <div className="text-center p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Secure</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300">End-to-end encryption and secure data storage</p>
                </div>
                <div className="text-center p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Private</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Your data stays private and is never shared</p>
                </div>
                <div className="text-center p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Reliable</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Built for performance and reliability</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Team Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl text-gray-900 dark:text-white">Our Team</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              Vegetablah.com is developed by a passionate team of health enthusiasts, developers, and data scientists 
              who believe in the power of data-driven health decisions. We're committed to continuously improving our platform 
              based on user feedback and the latest research in health and nutrition.
            </p>
          </CardContent>
        </Card>

        {/* Contact CTA */}
        <div className="text-center">
          <Card>
            <CardContent className="py-8">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Have Questions or Feedback?
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                We'd love to hear from you! Whether you have questions about using Vegetablah.com 
                or suggestions for new features, we're here to help.
              </p>
              <a 
                href="/contact" 
                className="inline-block bg-gray-700 text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors font-medium"
              >
                Get in Touch
              </a>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;